import { supabase } from '../lib/supabaseClient';
import type { StockBalance, StockLedgerEntry } from '../types/domain';

/**
 * Stock Ledger / Stock Balance — read-only in this vertical slice. Both
 * tables are populated only as a side effect of `postGoodsReceipt()` in
 * `purchasing.ts` (which calls the permission-gated
 * fn_post_goods_receipt_checked -> the unmodified fn_post_goods_receipt,
 * migration 0008). There is no direct-write path to either table from the
 * frontend, matching stock_ledger's own DB-level immutability
 * (UPDATE/DELETE blocked by trigger — migration 0008) and stock_balances'
 * trigger-maintained-projection design. Visibility is gated by RLS
 * (tenant isolation) plus the `stock_ledger.read` / `stock_balance.read`
 * permissions from migration 0032 for nav-level display.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */

/** branchId scopes to that branch's own rows (stock_ledger.branch_id is a
 * real FK); warehouseId narrows further to one warehouse (used by the
 * Stock Balance -> Stock Ledger drilldown). Both are optional and
 * independent. */
export async function listStockLedger(warehouseId?: string, branchId?: string): Promise<StockLedgerEntry[]> {
  let query = supabase
    .from('stock_ledger')
    .select(
      'ledger_id, tenant_id, branch_id, warehouse_id, item_id, uom_id, movement_type, qty_delta, ref_type, ref_id, occurred_at'
    )
    .order('occurred_at', { ascending: false })
    .limit(200);
  if (warehouseId) {
    query = query.eq('warehouse_id', warehouseId);
  }
  if (branchId) {
    query = query.eq('branch_id', branchId);
  }
  const { data, error } = await query;
  if (error) throw new Error(`listStockLedger failed: ${error.message}`);
  return (data ?? []) as StockLedgerEntry[];
}

/** stock_balances has no branch_id column (only warehouse_id) — pass the
 * active branch's warehouse ids (BranchContext's activeBranchWarehouseIds)
 * to scope to it. warehouseId narrows further to a single warehouse. */
export async function listStockBalances(warehouseId?: string, branchWarehouseIds?: string[]): Promise<StockBalance[]> {
  let query = supabase
    .from('stock_balances')
    .select('tenant_id, warehouse_id, item_id, qty_on_hand, updated_at')
    .order('updated_at', { ascending: false });
  if (warehouseId) {
    query = query.eq('warehouse_id', warehouseId);
  } else if (branchWarehouseIds) {
    query = query.in('warehouse_id', branchWarehouseIds);
  }
  const { data, error } = await query;
  if (error) throw new Error(`listStockBalances failed: ${error.message}`);
  return (data ?? []) as StockBalance[];
}

/**
 * Read-only counts for the FASE 6 tables (stock_counts/transfers/
 * waste_events) — RLS-scoped plain reads, same pattern as every other
 * reference-data read in this file. No create/post wrapper RPC exists yet
 * for these (FASE 6 application integration is not built), so this is
 * intentionally read-only: it lets the Warehouse dashboard show REAL row
 * counts (likely 0 today) rather than a fabricated placeholder number.
 */
export async function countPendingStockCounts(): Promise<number> {
  const { count, error } = await supabase.from('stock_counts').select('count_id', { count: 'exact', head: true }).neq('status', 'POSTED');
  if (error) throw new Error(`countPendingStockCounts failed: ${error.message}`);
  return count ?? 0;
}

export async function countPendingTransfers(): Promise<number> {
  const { count, error } = await supabase.from('transfers').select('transfer_id', { count: 'exact', head: true }).neq('status', 'POSTED');
  if (error) throw new Error(`countPendingTransfers failed: ${error.message}`);
  return count ?? 0;
}

export async function countPendingWaste(): Promise<number> {
  const { count, error } = await supabase.from('waste_events').select('waste_id', { count: 'exact', head: true }).neq('status', 'POSTED');
  if (error) throw new Error(`countPendingWaste failed: ${error.message}`);
  return count ?? 0;
}
